import { useEffect, useRef } from 'react'

import { useApp } from '../AppContext.jsx'

import { FETCH_STATUS } from '../../shared/constants/fetchStatus.js'

import { useThemeVars } from '../../domains/config'

const DEFAULT_SECTION_PADDING_FALLBACK = '1em'

export function useAppRootVars() {
  const {
    containerRef,
    configStatus,
    projectConfig,
    panelStyles,
    containerWidth,
  } = useApp()

  useThemeVars(projectConfig?.style_defaults, panelStyles, containerRef)

  useEffect(() => {
    if (configStatus !== FETCH_STATUS.READY) return

    const sectionPadding = projectConfig?.style_defaults?.section_padding ?? {}
    const {
      nav_bar_desktop,
      nav_bar_mobile,
      addend = 0,
      fallback = DEFAULT_SECTION_PADDING_FALLBACK,
    } = sectionPadding
    let frameId = null
    let resizeObserver = null
    let observedElement = null

    function disconnectResizeObserver() {
      resizeObserver?.disconnect()
      resizeObserver = null
      observedElement = null
    }

    function observeNavbar(element) {
      if (typeof ResizeObserver === 'undefined' || !element || observedElement === element) return

      disconnectResizeObserver()
      resizeObserver = new ResizeObserver(() => {
        scheduleMeasure()
      })
      resizeObserver.observe(element)
      observedElement = element
    }

    function measure() {
      const selectors = [nav_bar_desktop, nav_bar_mobile].filter(Boolean)
      for (const selector of selectors) {
        const el = document.querySelector(`.${selector}`)
        if (el && el.offsetHeight > 0) {
          observeNavbar(el)
          containerRef.current?.style.setProperty(
            '--bim-section-padding',
            `${el.offsetHeight + addend + 8}px`,
          )
          return
        }
      }

      disconnectResizeObserver()
      containerRef.current?.style.setProperty('--bim-section-padding', fallback)
    }

    function scheduleMeasure() {
      if (frameId != null) {
        window.cancelAnimationFrame(frameId)
      }

      frameId = window.requestAnimationFrame(() => {
        frameId = null
        measure()
      })
    }

    measure()
    scheduleMeasure()

    const mutationObserver = typeof MutationObserver === 'undefined'
      ? null
      : new MutationObserver(() => {
        scheduleMeasure()
      })

    mutationObserver?.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style'],
    })

    window.addEventListener('resize', scheduleMeasure)
    return () => {
      if (frameId != null) {
        window.cancelAnimationFrame(frameId)
      }
      disconnectResizeObserver()
      mutationObserver?.disconnect()
      window.removeEventListener('resize', scheduleMeasure)
    }
  }, [configStatus, containerRef, projectConfig])

  const basePrevRef = useRef(null)
  useEffect(() => {
    if (!containerRef.current || containerWidth == null) return

    const HYSTERESIS = 20
    const prev = basePrevRef.current
    let base

    if (prev === '16px') {
      base = containerWidth <= 1280 ? '12px'
        : containerWidth <= 1537 - HYSTERESIS ? '14px'
        : '16px'
    } else if (prev === '12px') {
      base = containerWidth > 1280 + HYSTERESIS
        ? (containerWidth > 1537 ? '16px' : '14px')
        : '12px'
    } else {
      base = containerWidth <= 1280 ? '12px' : containerWidth <= 1537 ? '14px' : '16px'
    }

    basePrevRef.current = base
    containerRef.current.style.setProperty('--bim-base', base)
  }, [containerRef, containerWidth])
}